// API 설정 파일
// axios.js의 baseURL과 동일 규칙: 개발 시 REACT_APP_API_URL이 CRA(3000)를 가리키면 백엔드(8080)로 보정.
// API_ENDPOINTS는 절대 URL이라, 여기만 다르면 api.get(USER_PROFILE)이 잘못된 호스트로 나가 AxiosError가 난다.
const ENV = process.env.REACT_APP_ENV || 'development';
const DEV_BACKEND_ORIGIN = 'http://localhost:8080';

const isLoopbackHost = (host) => host === 'localhost' || host === '127.0.0.1';
const resolveDevBackendOrigin = (hostname) =>
  isLoopbackHost(hostname) ? DEV_BACKEND_ORIGIN : `http://${hostname}:8080`;

let API_BASE_URL =
  process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : DEV_BACKEND_ORIGIN;

if (typeof window !== 'undefined' && ENV === 'development') {
  const origin = window.location.origin;
  const hostname = window.location.hostname;
  const backendOrigin = resolveDevBackendOrigin(hostname);

  if (!isLoopbackHost(hostname)) {
    try {
      if (isLoopbackHost(new URL(API_BASE_URL).hostname)) {
        API_BASE_URL = backendOrigin;
      }
    } catch {
      API_BASE_URL = backendOrigin;
    }
  }

  if (
    API_BASE_URL === origin ||
    API_BASE_URL === '' ||
    API_BASE_URL.startsWith('http://localhost:3000')
  ) {
    API_BASE_URL = backendOrigin;
  }
}

const apiPath = (path) => `${API_BASE_URL}${path.startsWith('/') ? path : '/' + path}`;

export const API_ENDPOINTS = {
  // 인증
  AUTH_CONFIG: apiPath('/api/auth/config'),
  LOGIN: apiPath('/api/auth/login'),
  TEST_LOGIN: apiPath('/api/auth/test-login'),
  AUTH_REFRESH: apiPath('/api/auth/refresh'),
  REGISTER: apiPath('/api/auth/register'),
  LOGOUT: apiPath('/api/auth/logout'),
  AUTH_FIND_EMAIL_BY_PHONE: apiPath('/api/auth/find-email-by-phone'),
  AUTH_FIND_PASSWORD_BY_PHONE: apiPath('/api/auth/find-password-by-phone'),

  // 사용자
  USER_PROFILE: apiPath('/api/users/profile'),
  USER_PROFILE_IS_ORG: apiPath('/api/users/profile/is-org'),
  USER_PROFILE_RESIGNATION_DATE: apiPath('/api/users/profile/resignation-date'),
  USER_UPDATE: apiPath('/api/users/profile'),
  USER_PROFILE_AVATAR: apiPath('/api/users/profile/avatar'),
  USER_AVATAR_BY_ID: (id) => apiPath(`/api/users/${id}/avatar`),
  USERS: apiPath('/api/users'),
  USERS_PAGE: apiPath('/api/users/page'),
  USERS_IS_PAGE: apiPath('/api/users/is-page'),
  USER_BY_ID: (id) => apiPath(`/api/users/${id}`),
  USER_PROFILE_CHANGE_HISTORY: (id) => apiPath(`/api/users/${id}/profile-change-history`),
  IS_USER_SETTING_CHANGE_HISTORY: (id) => apiPath(`/api/users/${id}/is-setting-change-history`),
  USERS_INDIVIDUAL: apiPath('/api/users/individual'),
  USERS_CORPORATE: apiPath('/api/users/corporate'),
  USERS_RECOMMENDED: apiPath('/api/users/recommended'),
  USERS_RECOMMENDED_PAGE: apiPath('/api/users/recommended/page'),
  USERS_SUPERVISOR_NAME_SEARCH: apiPath('/api/users/supervisor-name-search'),
  USERS_RECOMMENDED_BY_ID: (id) => apiPath(`/api/users/recommended/${id}`),
  USERS_AVAILABLE_ROLES: (type) => apiPath(`/api/users/available-roles/${type}`),
  USERS_CHECK_ROLES_PERMISSIONS: apiPath('/api/users/check-roles-permissions'),
  GA_INSU_USERS_PAGE: apiPath('/api/users/ga-insu-users/page'),
  GA_INSU_USER_RESIGNATION_DATE: (id) => apiPath(`/api/users/ga-insu-users/${id}/resignation-date`),
  GA_INSU_ORG_SETTINGS: apiPath('/api/users/ga-insu-org-settings'),
  MY_IS_ORG_TREE: apiPath('/api/users/my-is-org-tree'),

  // 알림
  NOTIFICATIONS: apiPath('/api/notifications'),
  NOTIFICATION_READ: (id) => apiPath(`/api/notifications/${id}/read`),

  // 공지
  NOTICES: apiPath('/api/notices'),
  NOTICE_BY_ID: (id) => apiPath(`/api/notices/${id}`),
  NOTICES_MAIN_MODAL: apiPath('/api/notices/main-modal'),
  NOTICE_ATTACHMENTS: (noticeId) => apiPath(`/api/notices/${noticeId}/attachments`),
  NOTICE_ATTACHMENT: (attachmentId) => apiPath(`/api/notices/attachments/${attachmentId}`),
  NOTICE_ATTACHMENT_DOWNLOAD: (attachmentId) => apiPath(`/api/notices/attachments/${attachmentId}/download`),
  NOTICE_THUMBNAIL: (id) => apiPath(`/api/notices/${id}/thumbnail`),

  // 시스템
  SYSTEM_STATUS: apiPath('/api/system/status'),
  SYSTEM_SETTINGS_SUMMARY_LLM_TYPE: apiPath('/api/system-settings/summary-llm-type'),
  LOGIN_LOGS: apiPath('/api/login-logs'),
  USER_RESIGNATION_TASKS: apiPath('/api/user-resignation-tasks'),
  USER_RESIGNATION_TASK_BY_ID: (id) => apiPath(`/api/user-resignation-tasks/${id}`),

  // STT/LLM 호출 기록 (OP)
  MEMO_SUMMARY_CALL_LOGS: apiPath('/api/memo-summary-call-logs'),
  MEMO_SUMMARY_CALL_LOGS_STATS: apiPath('/api/memo-summary-call-logs/stats'),
  MEMO_SUMMARY_CALL_LOG_BY_ID: (id) => apiPath(`/api/memo-summary-call-logs/${id}`),
  MEMO_SUMMARY_CALL_LOGS_BY_MONTH: apiPath('/api/memo-summary-call-logs/by-month'),

  // 현황판
  CORPORATE_DASHBOARD_STATS: apiPath('/api/corporate-dashboard/stats'),
  INDIVIDUAL_DASHBOARD_STATS: apiPath('/api/individual-dashboard/stats'),
  OPERATOR_DASHBOARD_STATS: apiPath('/api/operator-dashboard/stats'),

  // GA 리포트 통계
  REPORT_STATISTICS_USER_SCOPE_INFO: apiPath('/api/report-statistics/user/scope-info'),
  REPORT_STATISTICS_USER_STATS: apiPath('/api/report-statistics/user/stats'),
  REPORT_STATISTICS_ALL_FILTERS: apiPath('/api/report-statistics/all/filters'),
  REPORT_STATISTICS_ALL_STATS: apiPath('/api/report-statistics/all/stats'),
  REPORT_STATISTICS_USER_LIST: apiPath('/api/report-statistics/user/list'),
  REPORT_STATISTICS_USER_REQUEST_LIST: apiPath('/api/report-statistics/user/request-list'),
  REPORT_STATISTICS_USER_USER_ACTIVITY: apiPath('/api/report-statistics/user/user-activity'),
  REPORT_STATISTICS_USER_USERS_SEARCH: apiPath('/api/report-statistics/user/users'),
  REPORT_STATISTICS_ALL_LIST: apiPath('/api/report-statistics/all/list'),
  REPORT_STATISTICS_ALL_USER_ACTIVITY: apiPath('/api/report-statistics/all/user-activity'),
  REPORT_STATISTICS_ALL_REQUEST_LIST: apiPath('/api/report-statistics/all/request-list'),
  REPORT_STATISTICS_ALL_USERS_SEARCH: apiPath('/api/report-statistics/all/users'),

  REPORT_STATISTICS_BY_RECEIVER_FILTERS: apiPath('/api/report-statistics/by-receiver/filters'),
  REPORT_STATISTICS_BY_RECEIVER_STATS: apiPath('/api/report-statistics/by-receiver/stats'),
  REPORT_STATISTICS_BY_RECEIVER_WAITING_BY_INSURER: apiPath('/api/report-statistics/by-receiver/waiting-by-insurer'),
  REPORT_STATISTICS_BY_RECEIVER_WAITING_BY_INSURER_DAILY: apiPath('/api/report-statistics/by-receiver/waiting-by-insurer/daily'),
  REPORT_STATISTICS_BY_RECEIVER_PROCESSING_BY_INSURER: apiPath('/api/report-statistics/by-receiver/processing-by-insurer'),
  REPORT_STATISTICS_BY_RECEIVER_PROCESSING_BY_INSURER_RANGE: apiPath('/api/report-statistics/by-receiver/processing-by-insurer/range'),
  REPORT_STATISTICS_BY_RECEIVER_STATUS_LIST: apiPath('/api/report-statistics/by-receiver/status-list'),
  REPORT_STATISTICS_BY_GA_RECEIVER_FILTERS: apiPath('/api/report-statistics/by-ga-receiver/filters'),
  REPORT_STATISTICS_BY_GA_RECEIVER_STATS: apiPath('/api/report-statistics/by-ga-receiver/stats'),
  REPORT_STATISTICS_BY_GA_RECEIVER_STATUS_LIST: apiPath('/api/report-statistics/by-ga-receiver/status-list'),

  // IS 리포트 통계(관리자용)
  REPORT_STATISTICS_IS_ADMIN_ORG_TREE: apiPath('/api/report-statistics/is-admin/org-tree'),
  REPORT_STATISTICS_IS_ADMIN_FILTERS: apiPath('/api/report-statistics/is-admin/filters'),
  REPORT_STATISTICS_IS_ADMIN_STATS: apiPath('/api/report-statistics/is-admin/stats'),
  REPORT_STATISTICS_IS_ADMIN_LIST: apiPath('/api/report-statistics/is-admin/list'),
  REPORT_STATISTICS_IS_ADMIN_REQUEST_LIST: apiPath('/api/report-statistics/is-admin/request-list'),
  REPORT_STATISTICS_IS_ADMIN_USERS_SEARCH: apiPath('/api/report-statistics/is-admin/users'),

  // 채팅
  CHAT_UNREAD_COUNT: apiPath('/api/chat/unread-count'),
  CHAT_ROOMS: apiPath('/api/chat/rooms'),
  CHAT_ROOMS_SUMMARY: apiPath('/api/chat/rooms/summary'),
  CHAT_ROOMS_PAGE: apiPath('/api/chat/rooms/page'),
  CHAT_ROOMS_NEW: apiPath('/api/chat/rooms/new'),
  CHAT_ROOM_BY_ID: (id) => apiPath(`/api/chat/rooms/${id}`),
  CHAT_ROOM_MESSAGES_RANGE: (id) => apiPath(`/api/chat/rooms/${id}/messages/range`),
  CHAT_ROOM_EARLIEST_MESSAGE: (id) => apiPath(`/api/chat/rooms/${id}/messages/earliest`),
  CHAT_AVAILABLE: apiPath('/api/chat/available'),

  // 원수사/회사
  COMPANIES: apiPath('/api/companies'),
  COMPANY_DETAIL: (id) => apiPath(`/api/companies/${id}`),
  COMPANY_CHANGE_HISTORY: (id) => apiPath(`/api/companies/${id}/change-history`),
  COMPANY_SEARCH_BY_BUSINESS_NUMBER: apiPath('/api/companies/search/by-business-number'),
  COMPANY_SEARCH_BY_NAME: apiPath('/api/companies/search/by-name'),
  COMPANIES_AVAILABLE: apiPath('/api/companies/available'),
  COMPANY_ORG_NODES: (companyId) => apiPath(`/api/companies/${companyId}/org-nodes`),
  COMPANY_ORG_NODE_DETAIL: (companyId, nodeId) => apiPath(`/api/companies/${companyId}/org-nodes/${nodeId}`),
  COMPANY_ORG_NODE_ACTIVATE: (companyId, nodeId) => apiPath(`/api/companies/${companyId}/org-nodes/${nodeId}/activate`),

  // 일정
  EVENTS: apiPath('/api/events'),
  EVENTS_WEEKLY_COUNTS: apiPath('/api/events/weekly-counts'),
  EVENT_BY_ID: (id) => apiPath(`/api/events/${id}`),
  EVENTS_SYNC_FROM_GOOGLE: apiPath('/api/events/sync/from-google'),
  EVENTS_SYNC_TO_GOOGLE: apiPath('/api/events/sync/to-google'),
  EVENTS_SYNC_TRIGGER: apiPath('/api/events/sync/trigger'),
  EVENTS_SYNC_STATUS: apiPath('/api/events/sync/status'),

  // 공휴일
  HOLIDAYS: (year) => apiPath(`/api/holidays?year=${year}`),
  HOLIDAY_NAMES: (year) => apiPath(`/api/holidays/names?year=${year}`),
  HOLIDAY_MANAGEMENT: (year) => apiPath(`/api/holiday-management?year=${year}`),
  HOLIDAY_MANAGEMENT_CUSTOM: apiPath('/api/holiday-management/custom'),
  HOLIDAY_MANAGEMENT_DISABLE: (holidayDate) => apiPath(`/api/holiday-management/${holidayDate}/disable`),
  HOLIDAY_MANAGEMENT_RESTORE: (holidayDate) => apiPath(`/api/holiday-management/${holidayDate}/restore`),

  // 설계요청
  DESIGN_REQUESTS: apiPath('/api/design-requests'),
  DESIGN_REQUEST_BY_ID: (id) => apiPath(`/api/design-requests/${id}`),
  DESIGN_REQUESTS_MY_OWN: apiPath('/api/design-requests/my-own-requests'),
  DESIGN_REQUESTS_SUBORDINATE: apiPath('/api/design-requests/subordinate-requests'),
  DESIGN_REQUESTS_CORPORATE: apiPath('/api/design-requests/corporate-requests'),
  DESIGN_REQUESTS_CORPORATE_ORG_TREE: apiPath('/api/design-requests/corporate-requests/org-tree'),
  DESIGN_REQUESTS_CHECK_DUPLICATE: apiPath('/api/design-requests/check-duplicate'),
  DESIGN_REQUEST_COPY_PREFILL: (designRequestId) => apiPath(`/api/design-requests/${designRequestId}/copy-prefill`),
  DESIGN_REQUEST_ATTACHMENT_DOWNLOAD: (attachmentId) =>
    apiPath(`/api/design-requests/attachments/${attachmentId}/download`),
  DESIGN_REQUESTS_PENDING_COUNT: (userId) => apiPath(`/api/design-requests/pending-count/${userId}`),
  DESIGN_RESULT_SUPPLEMENT_REQUEST: (designResultId) => apiPath(`/api/design-requests/results/${designResultId}/supplement-request`),
  DESIGN_RESULT_SUBMISSION_HISTORY: (designResultId) => apiPath(`/api/design-requests/results/${designResultId}/submission-history`),
  DESIGN_RESULT_SUBMISSION_HISTORY_DETAIL: (historyId) => apiPath(`/api/design-requests/results/submission-history/${historyId}`),
  DESIGN_RESULT_SUBMISSION_HISTORY_ATTACHMENT_DOWNLOAD: (historyId, attachmentIndex) =>
    apiPath(`/api/design-requests/results/submission-history/${historyId}/attachments/${attachmentIndex}/download`),
  DESIGN_RESULT_UPDATE: (designResultId) => apiPath(`/api/design-requests/results/${designResultId}/edit`),
  DESIGN_RESULT_CONTENT_UPDATE: (designResultId) => apiPath(`/api/design-requests/results/${designResultId}/content`),
  DESIGN_RESULT_CONFIRMED_ATTACHMENT_UPDATE: (designResultId) =>
    apiPath(`/api/design-requests/results/${designResultId}/confirmed-attachment`),
  DESIGN_RESULT_BASIC_INFO_UPDATE: (designResultId) => apiPath(`/api/design-requests/results/${designResultId}/basic-info`),
  DESIGN_RESULT_CONTENT_HISTORY: (designResultId) => apiPath(`/api/design-requests/results/${designResultId}/content-history`),
  DESIGN_RESULT_CONTENT_HISTORY_DETAIL: (historyId) => apiPath(`/api/design-requests/results/content-history/${historyId}`),
  DESIGN_RESULT_CONTENT_HISTORY_ATTACHMENT_DOWNLOAD: (historyId, attachmentIndex) =>
    apiPath(`/api/design-requests/results/content-history/${historyId}/attachments/${attachmentIndex}/download`),
  DESIGN_REQUEST_CONTENT_UPDATE: (designRequestId) => apiPath(`/api/design-requests/${designRequestId}/content`),
  DESIGN_REQUEST_CONTENT_HISTORY: (designRequestId) => apiPath(`/api/design-requests/${designRequestId}/content-history`),
  DESIGN_REQUEST_CONTENT_HISTORY_DETAIL: (historyId) => apiPath(`/api/design-requests/content-history/${historyId}`),
  DESIGN_REQUEST_CONTENT_HISTORY_ATTACHMENT_DOWNLOAD: (historyId, attachmentIndex) =>
    apiPath(`/api/design-requests/content-history/${historyId}/attachments/${attachmentIndex}/download`),

  // 청약요청
  SUBSCRIPTION_REQUESTS: apiPath('/api/subscription-requests'),
  SUBSCRIPTION_REQUESTS_CORPORATE_ORG_TREE: apiPath('/api/subscription-requests/corporate-requests/org-tree'),
  SUBSCRIPTION_REQUEST_BY_ID: (id) => apiPath(`/api/subscription-requests/${id}`),
  SUBSCRIPTION_REQUEST_CONTENT_UPDATE: (id) => apiPath(`/api/subscription-requests/${id}/content`),
  SUBSCRIPTION_REQUEST_CONTENT_HISTORY: (id) => apiPath(`/api/subscription-requests/${id}/content-history`),
  SUBSCRIPTION_REQUEST_CONTENT_HISTORY_DETAIL: (historyId) =>
    apiPath(`/api/subscription-requests/content-history/${historyId}`),
  SUBSCRIPTION_REQUEST_CONTENT_HISTORY_ATTACHMENT_DOWNLOAD: (historyId, attachmentIndex) =>
    apiPath(`/api/subscription-requests/content-history/${historyId}/attachments/${attachmentIndex}/download`),
  SUBSCRIPTION_REQUEST_ATTACHMENT_DOWNLOAD: (attachmentId) =>
    apiPath(`/api/subscription-requests/attachments/${attachmentId}/download`),
  SUBSCRIPTION_RESULT_UPDATE: (subscriptionResultId) =>
    apiPath(`/api/subscription-requests/results/${subscriptionResultId}/edit`),
  SUBSCRIPTION_RESULT_CONTENT_UPDATE: (subscriptionResultId) =>
    apiPath(`/api/subscription-requests/results/${subscriptionResultId}/content`),
  SUBSCRIPTION_RESULT_CONTENT_HISTORY: (subscriptionResultId) =>
    apiPath(`/api/subscription-requests/results/${subscriptionResultId}/content-history`),
  SUBSCRIPTION_RESULT_CONTENT_HISTORY_DETAIL: (historyId) =>
    apiPath(`/api/subscription-requests/results/content-history/${historyId}`),
  SUBSCRIPTION_RESULT_CONTENT_HISTORY_ATTACHMENT_DOWNLOAD: (historyId, attachmentIndex) =>
    apiPath(`/api/subscription-requests/results/content-history/${historyId}/attachments/${attachmentIndex}/download`),
  SUBSCRIPTION_REQUESTS_SUBORDINATE: apiPath('/api/subscription-requests/subordinate-requests'),
  SUBSCRIPTION_REQUESTS_BY_DESIGN_RESULT: (designResultId) => apiPath(`/api/subscription-requests/by-design-result/${designResultId}`),

  // 상품 카테고리
  PRODUCT_CATEGORIES: apiPath('/api/product-categories'),
  PRODUCT_CATEGORIES_CATEGORY2: apiPath('/api/product-categories/category2'),

  // 에이전트 코드
  AGENT_CODES: apiPath('/api/agent-codes'),
  AGENT_CODES_MY_CODES: apiPath('/api/agent-codes/my-codes'),
  AGENT_CODES_BY_ID: (id) => apiPath(`/api/agent-codes/${id}`),
  AGENT_CODES_USER: (userId) => apiPath(`/api/agent-codes/user/${userId}`),

  // 지역
  REGIONS: apiPath('/api/regions'),
  REGIONS_ALL: apiPath('/api/regions/all'),
  REGIONS_SEARCH: apiPath('/api/regions/search'),
  REGION_BY_ID: (id) => apiPath(`/api/regions/${id}`),
  REGION_RESTORE: (id) => apiPath(`/api/regions/${id}/restore`),

  // GA 조직
  GA_ORGANIZATIONS: apiPath('/api/ga-organizations'),
  GA_ORGANIZATION_BY_ID: (id) => apiPath(`/api/ga-organizations/${id}`),

  // 회사타입/역할/권한
  COMPANY_TYPE_ROLES: (type) => apiPath(`/api/company-type-roles/${type}`),
  COMPANY_TYPE_ROLE_UPDATE: (companyType, userRole) => apiPath(`/api/company-type-roles/${companyType}/${userRole}`),
  PERMISSIONS: apiPath('/api/permissions'),
  PERMISSION_BY_ID: (id) => apiPath(`/api/permissions/${id}`),
  PERMISSIONS_ACTIVE: (companyType) => apiPath(`/api/permissions/active/${companyType}`),
  PERMISSIONS_ROLE: (companyType, userRole) => apiPath(`/api/permissions/role/${companyType}/${userRole}`),
  PERMISSIONS_ASSIGN: apiPath('/api/permissions/assign'),

  // 초대
  INVITATIONS_VALIDATE: (code) => apiPath(`/api/invitations/validate/${code}`),
  INVITATIONS_REGISTER: apiPath('/api/invitations/register'),

  // 사업장
  DIVISIONS: apiPath('/api/divisions'),
  DIVISIONS_COMPANY: (companyId) => apiPath(`/api/divisions/company/${companyId}`),

  // 연락처/고객
  CUSTOMERS: apiPath('/api/customers'),
  CUSTOMERS_SEARCH: apiPath('/api/customers/search'),
  CUSTOMERS_EXTRACT_BUSINESS_CARD: apiPath('/api/customers/extract-business-card'),
  CUSTOMER_BY_ID: (id) => apiPath(`/api/customers/${id}`),
  CUSTOMER_FOR_EDIT: (id) => apiPath(`/api/customers/${id}/for-edit`),
  CUSTOMER_BUSINESS_CARD: (id) => apiPath(`/api/customers/${id}/business-card`),
  CUSTOMER_GROUPS: apiPath('/api/customer-groups'),
  CUSTOMER_GROUP_BY_ID: (id) => apiPath(`/api/customer-groups/${id}`),
  CUSTOMER_FILES: (customerId) => apiPath(`/api/customers/${customerId}/files`),
  CUSTOMER_FILE: (customerId, fileId) => apiPath(`/api/customers/${customerId}/files/${fileId}`),
  CUSTOMER_FILE_DOWNLOAD: (customerId, fileId) =>
    apiPath(`/api/customers/${customerId}/files/${fileId}/download`),

  // 메모
  MEMOS: apiPath('/api/memos'),
  MEMO_FILE: (memoId) => apiPath(`/api/memos/${memoId}/file`),
  MEMO_CREATE_EVENT: (memoId) => apiPath(`/api/memos/${memoId}/create-event`),

  // 설계요청 수신자 (그룹매핑 기준, 레거시 URL /api/mappings/... 유지)
  DESIGN_RECEIVER_USERS: apiPath('/api/mappings/my-receivers'),
  /** @deprecated DESIGN_RECEIVER_USERS 사용 권장 */
  MAPPINGS_MY_RECEIVERS: apiPath('/api/mappings/my-receivers'),

  // 개인정보처리방침 (로그인 화면 등 비인증 공개)
  PERSONAL_INFO_POLICY_LATEST_PUBLIC: apiPath('/api/personal-info-policies/latest-public'),
  // 서비스 이용약관 (비인증 공개, 시행일 기준 최신)
  SERVICE_TERMS_LATEST_PUBLIC: apiPath('/api/service-terms/latest-public'),
  SERVICE_TERMS_VERSIONS_PUBLIC: apiPath('/api/service-terms/versions-public'),
  SERVICE_TERMS_PUBLIC: (id) => apiPath(`/api/service-terms/public/${id}`),

  // 코드관리 (OP 전용)
  CODE_TYPES: apiPath('/api/code-management/types'),
  CODE_TYPE_BY_ID: (id) => apiPath(`/api/code-management/types/${id}`),
  CODE_VALUES_BY_TYPE: (typeId) => apiPath(`/api/code-management/types/${typeId}/values`),
  CODE_VALUES: apiPath('/api/code-management/values'),
  CODE_VALUE_BY_ID: (id) => apiPath(`/api/code-management/values/${id}`),

  // 프로모션
  PROMOTIONS: apiPath('/api/promotions'),
  PROMOTION_BY_ID: (id) => apiPath(`/api/promotions/${id}`),
  PROMOTION_TERMINATE: (id) => apiPath(`/api/promotions/${id}/terminate`),
  PROMOTION_IMAGE: (id) => apiPath(`/api/promotions/${id}/image`),
  PROMOTION_THUMBNAIL: (id) => apiPath(`/api/promotions/${id}/thumbnail`),
  PROMOTION_THUMBNAILS: (id) => apiPath(`/api/promotions/${id}/thumbnails`),
  PROMOTION_THUMBNAIL_BY_ID: (promotionId, thumbnailId) =>
    apiPath(`/api/promotions/${promotionId}/thumbnails/${thumbnailId}`),
  PROMOTION_THUMBNAIL_DISPLAY_STATUS: (promotionId, thumbnailId) =>
    apiPath(`/api/promotions/${promotionId}/thumbnails/${thumbnailId}/display-status`),
  PROMOTION_THUMBNAIL_SORT_ORDERS: (id) => apiPath(`/api/promotions/${id}/thumbnail-sort-orders`),
  PROMOTION_DESIGN_REQUEST_THUMBNAIL: (id) =>
    apiPath(`/api/promotions/${id}/design-request-thumbnail`),
  PROMOTION_POPUP_MEDIA: (id) => apiPath(`/api/promotions/${id}/popup-media`),
  PROMOTION_DESIGN_REQUEST_THUMBNAIL_DISPLAY_STATUS: (id) =>
    apiPath(`/api/promotions/${id}/design-request-thumbnail/display-status`),
  PROMOTION_ATTACHMENTS: (id) => apiPath(`/api/promotions/${id}/attachments`),
  PROMOTION_ATTACHMENT: (attachmentId) => apiPath(`/api/promotions/attachments/${attachmentId}`),
  PROMOTION_ATTACHMENT_DOWNLOAD: (attachmentId) => apiPath(`/api/promotions/attachments/${attachmentId}/download`),
  PROMOTION_STATS: (id) => apiPath(`/api/promotions/${id}/stats`),
  PROMOTION_CLICKS: (id) => apiPath(`/api/promotions/${id}/clicks`),
  PROMOTION_CLICK: (id, clickId) => apiPath(`/api/promotions/${id}/clicks/${clickId}`),
  PROMOTIONS_EXPOSURE: apiPath('/api/promotions/exposure'),
  PROMOTIONS_EXPOSURES: apiPath('/api/promotions/exposures'),
  PROMOTION_GA_DETAIL: (id) => apiPath(`/api/promotions/${id}/ga-detail`),
  PROMOTION_EXPOSURE_PRICE_QUOTE: apiPath('/api/promotions/exposure-price-quote'),
  PROMOTION_RATE_POLICIES: apiPath('/api/promotion-rate-policies'),
  PROMOTION_RATE_POLICY_BY_ID: (id) => apiPath(`/api/promotion-rate-policies/${id}`),
  CODE_LOOKUP_VALUES: (typeCode) => apiPath(`/api/code-lookup/types/${typeCode}/values`),
};

export default API_BASE_URL;
